import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, CheckCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';

// ── Turnstile Widget ─────────────────────────────────────────
function TurnstileWidget({ siteKey, onVerify }) {
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!siteKey || siteKey === '') {
      // No site key configured — skip captcha
      onVerify('BYPASS');
      return;
    }

    const render = () => {
      if (!window.turnstile || !ref.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: token => onVerify(token),
        'expired-callback': () => onVerify(null),
        theme: 'light',
      });
    };

    if (window.turnstile) {
      render();
    } else {
      // Load Turnstile script
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    }

    return () => {
      if (widgetId.current != null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} style={{ margin: '12px 0' }} />;
}

// ── Main Page ────────────────────────────────────────────────
export default function GuestSubmitPage() {
  const navigate = useNavigate();
  const [config, setConfig]           = useState(null);
  const [categories, setCategories]   = useState([]);
  const [priorities, setPriorities]   = useState([]);
  const [turnstileToken, setTurnstile] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(null); // { ticket_number, guest_email }

  // Account creation after submit
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [newPassword, setNewPassword]         = useState('');
  const [registering, setRegistering]         = useState(false);
  const [accountCreated, setAccountCreated]   = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    subject: '', description: '', category_id: '', priority_id: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/guest/config').then(d => setConfig(d.settings)).catch(() => {});
    api.get('/lookups/categories').then(d => setCategories(d.categories || [])).catch(() => {});
    api.get('/lookups/priorities').then(d => setPriorities(d.priorities || [])).catch(() => {});
  }, []);

  const logoUrl = config?.logo_url
    ? (config.logo_url.startsWith('/uploads/') ? `/api${config.logo_url}` : config.logo_url)
    : null;
  const siteName   = config?.site_name   || 'GT Computing';
  const siteKey    = config?.turnstile_site_key || '';
  const needCaptcha = !!siteKey;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.subject || !form.description) {
      return toast.error('Please fill in all required fields');
    }
    if (needCaptcha && !turnstileToken) {
      return toast.error('Please complete the CAPTCHA');
    }
    setSubmitting(true);
    try {
      const result = await api.post('/guest/tickets', {
        ...form,
        turnstile_token: turnstileToken || 'BYPASS',
      });
      setSubmitted(result);
    } catch (err) {
      toast.error(err.message || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setRegistering(true);
    try {
      await api.post('/guest/register', { email: submitted.guest_email, password: newPassword });
      setAccountCreated(true);
      toast.success('Account created! You can now log in.');
    } catch (err) {
      toast.error(err.message || 'Failed to create account');
    } finally {
      setRegistering(false);
    }
  };

  // ── Success screen ──
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}>
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.5 }} />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480 }}>
          <div className="card card-lg" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <CheckCircle size={48} color="var(--accent)" />
            </div>
            <h2 style={{ fontSize: 20, marginBottom: 8 }}>Ticket Submitted!</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>
              Your ticket number is <strong>#{submitted.ticket_number}</strong>.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
              We've sent a confirmation to <strong>{submitted.guest_email}</strong>. We'll be in touch soon.
            </p>

            {!accountCreated && !creatingAccount && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                <p style={{ fontSize: 13.5, marginBottom: 12, fontWeight: 500 }}>Want to track this ticket online?</p>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>Create a free account to check status and reply to updates.</p>
                <div className="flex gap-2" style={{ justifyContent: 'center' }}>
                  <button className="btn btn-secondary" onClick={() => navigate('/login')}>No thanks</button>
                  <button className="btn btn-primary" onClick={() => setCreatingAccount(true)}>Create account</button>
                </div>
              </div>
            )}

            {creatingAccount && !accountCreated && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                <p style={{ fontSize: 13.5, marginBottom: 16, fontWeight: 500 }}>Set a password for <strong>{submitted.guest_email}</strong></p>
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label className="form-label">Password (min 6 characters)</label>
                  <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" autoFocus />
                </div>
                <div className="flex gap-2" style={{ justifyContent: 'center' }}>
                  <button className="btn btn-secondary" onClick={() => setCreatingAccount(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleRegister} disabled={registering}>
                    {registering ? 'Creating…' : 'Create Account'}
                  </button>
                </div>
              </div>
            )}

            {accountCreated && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                <p style={{ color: 'var(--accent)', fontWeight: 500, marginBottom: 12 }}>✓ Account created successfully!</p>
                <button className="btn btn-primary" onClick={() => navigate('/login')}>Sign in now</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '24px 16px' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.5 }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} style={{ maxHeight: 56, maxWidth: 200, objectFit: 'contain', marginBottom: 16 }} onError={e => e.target.style.display = 'none'} />
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, background: 'var(--accent)', borderRadius: 12, marginBottom: 16 }}>
              <Zap size={20} color="white" />
            </div>
          )}
          <h1 style={{ fontSize: 22, letterSpacing: '-0.04em', marginBottom: 4 }}>{siteName}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Submit a Support Ticket</p>
        </div>

        <div className="card card-lg">
          <div className="flex items-center gap-3" style={{ marginBottom: 20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}><ArrowLeft size={15} /></button>
            <h2 style={{ fontSize: 17 }}>New Support Request</h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Contact info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input className="form-input" placeholder="Jane Smith" value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input className="form-input" type="email" placeholder="jane@example.com" value={form.email} onChange={e => set('email', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-input" type="tel" placeholder="(555) 555-5555" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <input className="form-input" placeholder="Acme Corp" value={form.company} onChange={e => set('company', e.target.value)} />
              </div>
            </div>

            <hr style={{ margin: '8px 0 20px', borderColor: 'var(--border)' }} />

            {/* Ticket info */}
            <div className="form-group">
              <label className="form-label">Subject *</label>
              <input className="form-input" placeholder="Briefly describe your issue" value={form.subject} onChange={e => set('subject', e.target.value)} required autoFocus />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                  <option value="">Select category…</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-select" value={form.priority_id} onChange={e => set('priority_id', e.target.value)}>
                  <option value="">Select priority…</option>
                  {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                style={{ minHeight: 160 }}
                placeholder="Please describe your issue in as much detail as possible. Include any error messages, what you were doing when it happened, and what you've already tried."
                value={form.description}
                onChange={e => set('description', e.target.value)}
                required
              />
            </div>

            {/* Turnstile */}
            <TurnstileWidget siteKey={siteKey} onVerify={setTurnstile} />

            <div className="flex gap-3" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/login')}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (needCaptcha && !turnstileToken)}>
                {submitting ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Submitting…</> : 'Submit Ticket'}
              </button>
            </div>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, color: 'var(--text-muted)', fontSize: 12 }}>
          Already have an account? <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, padding: '2px 6px' }} onClick={() => navigate('/login')}>Sign in</button>
        </p>
      </div>
    </div>
  );
}
